import { useSelector, useDispatch } from 'react-redux';
import { useCallback } from 'react';
import FJActionAIAudioGenerator from '../../../../redux/action/FJActionAIAudioGenerator';
import { setSliceIsDraggingFileAction, setSliceSelectListAction } from '../../../../redux/slices/FJSliceCommon';
import { setSliceIsDraggingVoiceFile } from '../../../../redux/slices/FJSliceAIAudioGenerator';
import FJActionModal from '../../../../redux/action/FJActionModal';
import FJAudioPlayManager from '../../../../store/FJAudioPlayManager';
import FJAIAudioLeftM2M from '../view/FJAIAudioLeftM2M';
import FJActionEditPage from '../../../../redux/action/FJActionEditPage';
import {
    CLOUD_TRANSCODE_SUPPORTED_FILE_TYPE_AUDIOS,
    UPLOAD_FILE_TYPE_OF_AUDIO,
} from '../../../../config/dataConfig/FJGenerateData.js';

const FJAIAudioLeftM2MContainer = () => {
    const dispatch = useDispatch();

    const subTabIndex = useSelector(state => state.AIAudioGenerator.subTabIndex);
    const audioInfo = useSelector(state => state.AIAudioGenerator.audioInfo);
    const uploadAudioStatus = useSelector(state => state.AIAudioGenerator.uploadAudioStatus);
    const vocalAudioInfo = useSelector(state => state.AIAudioGenerator.vocalAudioInfo);
    const uploadVocalAudioStatus = useSelector(state => state.AIAudioGenerator.uploadVocalAudioStatus);
    const incidentalAudioInfo = useSelector(state => state.AIAudioGenerator.incidentalAudioInfo);
    const uploadIncidentalAudioStatus = useSelector(state => state.AIAudioGenerator.uploadIncidentalAudioStatus);
    const isDraggingFile = useSelector(state => state.common.isDraggingFile);
    const isDraggingVoiceFile = useSelector(state => state.AIAudioGenerator.isDraggingVoiceFile);
    const m2mLyricContent = useSelector(state => state.AIAudioGenerator.m2mLyricContent);

    const lyricDesc = useSelector(state => state.AIAudioGenerator.lyricDesc);
    const isGenerateLyricsLoading = useSelector(state => state.AIAudioGenerator.isGenerateLyricsLoading);

    const setIsDraggingVoiceFile = useCallback(
        value => {
            dispatch(setSliceIsDraggingVoiceFile(value));
        },
        [dispatch],
    );

    const setIsDraggingFile = useCallback(
        value => {
            dispatch(setSliceIsDraggingFileAction(value));
        },
        [dispatch],
    );

    const uploadAudioFile = file => {
        dispatch(FJActionAIAudioGenerator.uploadAudioFileAction(file));
    };

    const uploadVocalAudioFile = file => {
        dispatch(FJActionAIAudioGenerator.uploadVocalAudioFileAction(file));
    };

    const uploadIncidentalAudioFile = file => {
        dispatch(FJActionAIAudioGenerator.uploadIncidentalAudioFileAction(file));
    };

    const onLyricContentChange = value => {
        dispatch(FJActionAIAudioGenerator.changeM2MLyricContentAction(value));
    };

    const onLyricDescChange = value => {
        dispatch(FJActionAIAudioGenerator.onLyricDescChangeAction(value));
    };

    const clearAudioInfo = () => {
        dispatch(FJActionAIAudioGenerator.clearAudioInfoAction());
    };

    const clearVocalAudioInfo = () => {
        dispatch(FJActionAIAudioGenerator.clearVocalAudioInfoAction());
    };

    const clearIncidentalAudioInfo = () => {
        dispatch(FJActionAIAudioGenerator.clearIncidentalAudioInfoAction());
    };

    const generateLyrics = () => {
        dispatch(FJActionAIAudioGenerator.generateLyricAction());
    };

    const onRecordClick = () => {
        FJAudioPlayManager.pauseAll();
        dispatch(FJActionModal.showRecordModalAction());
    };

    const onUseSampleInIncidentalFile = () => {
        dispatch(FJActionAIAudioGenerator.onUseSampleInIncidentalFileAction());
    };

    const onUseSampleInAudioFile = () => {
        dispatch(FJActionAIAudioGenerator.onUseSampleInAudioFileAction());
    };

    const onUseSampleInVoiceFile = () => {
        dispatch(FJActionAIAudioGenerator.onUseSampleInVoiceFileAction());
    };

    const onChangeSubTab = useCallback(
        value => {
            dispatch(FJActionAIAudioGenerator.changeSubTabIndexAction(value));
            FJAudioPlayManager.pauseAll();

            dispatch(setSliceSelectListAction([]));
            dispatch(FJActionAIAudioGenerator.setIsMultipleModeAction(false));
        },
        [dispatch],
    );

    const openMediaLibrary = type => {
        dispatch(
            FJActionEditPage.openMediaLibraryAction({
                uploadConfig: {
                    isMultiple: false,
                    maxMediaNum: 1,
                    selectedMediaIds: [],
                    acceptTypeWithTranscode: UPLOAD_FILE_TYPE_OF_AUDIO + CLOUD_TRANSCODE_SUPPORTED_FILE_TYPE_AUDIOS,
                    acceptTypeNoTranscode: '',
                    suffixTranscode: CLOUD_TRANSCODE_SUPPORTED_FILE_TYPE_AUDIOS,
                    // 媒体库的过滤规则
                    mediaFilterFunc: item => {
                        const mediaTypes = ['AUDIO'];
                        return mediaTypes.includes(item.type);
                    },
                    // 上传文件之后返回的数据
                    callbackOptions: {
                        audioAddType: type,
                    },
                },
            }),
        );
    };

    return (
        <FJAIAudioLeftM2M
            subTabIndex={subTabIndex}
            onChangeSubTab={onChangeSubTab}
            // 歌声相关
            audioInfo={audioInfo}
            uploadAudioStatus={uploadAudioStatus}
            uploadAudioFile={uploadAudioFile}
            clearAudioInfo={clearAudioInfo}
            vocalAudioInfo={vocalAudioInfo}
            uploadVocalAudioStatus={uploadVocalAudioStatus}
            uploadVocalAudioFile={uploadVocalAudioFile}
            clearVocalAudioInfo={clearVocalAudioInfo}
            onRecordClick={onRecordClick}
            // 歌词相关
            lyricContent={m2mLyricContent}
            onLyricContentChange={onLyricContentChange}
            lyricDesc={lyricDesc}
            onLyricDescChange={onLyricDescChange}
            generateLyrics={generateLyrics}
            isGenerateLyricsLoading={isGenerateLyricsLoading}
            // 配乐相关
            incidentalAudioInfo={incidentalAudioInfo}
            uploadIncidentalAudioStatus={uploadIncidentalAudioStatus}
            uploadIncidentalAudioFile={uploadIncidentalAudioFile}
            clearIncidentalAudioInfo={clearIncidentalAudioInfo}
            isDraggingFile={isDraggingFile}
            setIsDraggingFile={setIsDraggingFile}
            isDraggingVoiceFile={isDraggingVoiceFile}
            setIsDraggingVoiceFile={setIsDraggingVoiceFile}
            // 其他功能
            onUseSampleInAudioFile={onUseSampleInAudioFile}
            onUseSampleInVoiceFile={onUseSampleInVoiceFile}
            onUseSampleInIncidentalFile={onUseSampleInIncidentalFile}
            openMediaLibraryByIncidentalAudio={() => {
                openMediaLibrary('incidentalAudio');
            }}
            openMediaLibraryByAudio={() => {
                openMediaLibrary('music');
            }}
            openMediaLibraryByVocalAudio={() => {
                openMediaLibrary('vocal');
            }}
        />
    );
};

export default FJAIAudioLeftM2MContainer;
